import React, { useState, useRef, useEffect } from "react";
import { LayoutDashboard, LogOut } from "lucide-react";
import styles from "./Navbar.module.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../common/Button";
import SearchModal from "../common/SearchModal";
import logo from "../../assets/Sankrutilogo.jpeg";

const Navbar = () => {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("user");
    setUserInfo(null);
    setIsDropdownOpen(false);
    navigate("/login");
  };

  useEffect(() => {
    const fetchUser = () => {
      const userInfoString = localStorage.getItem("userInfo") || localStorage.getItem("user");
      if (userInfoString) {
        try {
          setUserInfo(JSON.parse(userInfoString));
        } catch (e) {
          console.error("Error parsing user info:", e);
        }
      }
    };
    
    fetchUser();
    window.addEventListener('profileUpdated', fetchUser);
    return () => window.removeEventListener('profileUpdated', fetchUser);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    return location.pathname === path ? styles.active : "";
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className={styles.navbar}>
      <Link
        to="/"
        className={styles.logoContainer}
        style={{ textDecoration: "none" }}
      >
        <div className={styles.logoIcon}>
          <img
            src={logo}
            alt="Sanskriti Shubh Aangan"
            className={styles.logo}
          />
        </div>
      </Link>



      <div
        className={`${styles.menuWrapper} ${isMobileMenuOpen ? styles.menuOpen : ""}`}
      >
        <ul className={styles.navLinks}>
          <li>
            <Link
              to="/"
              className={isActive("/")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              className={isActive("/about")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to="/amenities"
              className={isActive("/amenities")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Amenities
            </Link>
          </li>
          <li>
            <Link
              to="/events"
              className={isActive("/events")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Events
            </Link>
          </li>
          <li>
            <Link
              to="/notices"
              className={isActive("/notices")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Notices
            </Link>
          </li>
          <li>
            <Link
              to="/business"
              className={isActive("/business")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Business
            </Link>
          </li>
          <li>
            <Link
              to="/gallery"
              className={isActive("/gallery")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Gallery
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              className={isActive("/contact")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </li>
          <li className={styles.mobileOnlyAuth}>
            {userInfo ? (
              <div className={styles.mobileAuthLinks}>
                <Link 
                  to={`/${userInfo.role}`} 
                  className={styles.mobileAuthLink}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <LayoutDashboard size={18} style={{ marginRight: '8px' }} />
                  Dashboard
                </Link>
                <button 
                  className={`${styles.mobileAuthLink} ${styles.logoutBtn}`}
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                >
                  <LogOut size={18} style={{ marginRight: '8px' }} />
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className={styles.registerBtn} onClick={() => setIsMobileMenuOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                <span>Login / Sign Up</span>
                <div className={styles.btnDivider}></div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </Link>
            )}
          </li>
        </ul>
      </div>

      <div className={styles.navActions}>
        <button
          className={styles.searchIconBtn}
          onClick={() => setIsSearchOpen(true)}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </button>

        <div className={`${styles.authButtons} ${styles.desktopOnlyAuth}`} ref={dropdownRef}>
          {userInfo ? (
            <div className={styles.avatarContainer}>
              <button 
                className={styles.avatarBtn} 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <div className={styles.avatar}>
                  {userInfo.profileImage && userInfo.profileImage.startsWith('/uploads') ? (
                    <img src={`${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${userInfo.profileImage}`} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : userInfo.profileImage ? (
                    <img src={userInfo.profileImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    userInfo.fullName ? userInfo.fullName.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
              </button>
              
              {isDropdownOpen && (
                <div className={styles.profileDropdown}>
                  <div className={styles.dropdownHeader}>
                    <p className={styles.userName}>{userInfo.fullName || 'User'}</p>
                    <p className={styles.userRole}>{userInfo.role || 'Resident'}</p>
                  </div>
                  <div className={styles.dropdownDivider}></div>
                  <Link 
                    to={`/${userInfo.role}`} 
                    className={styles.dropdownItem}
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"></rect><rect x="14" y="3" width="7" height="5"></rect><rect x="14" y="12" width="7" height="9"></rect><rect x="3" y="16" width="7" height="5"></rect></svg>
                    Dashboard
                  </Link>
                  <button 
                    className={`${styles.dropdownItem} ${styles.logoutBtn}`}
                    onClick={handleLogout}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className={styles.registerBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              <span>Login / Sign Up</span>
              <div className={styles.btnDivider}></div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
            </Link>
          )}
        </div>
      </div>

      <button
        className={styles.hamburgerBtn}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? (
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        )}
      </button>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
